import { useEffect, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Actions from '../store/actions';

/**
 * useInstanceAvailability Hook
 * 
 * Provides zone availability checking based on selected instance types.
 * Uses Redux actions and store following project patterns.
 * 
 * Grays out zones where any of the selected instances (head node + compute nodes) 
 * are not available.
 * 
 * Usage:
 * const {
 *   zoneAvailability,
 *   isZoneAvailable,
 *   unavailableZones,
 *   isLoading,
 *   error,
 * } = useInstanceAvailability({
 *   provider: 'aws',
 *   credentialId: 'xxxxx',
 *   region: 'us-east-1',
 *   headNodeInstance: 't3.large',
 *   computeNodeInstances: ['t3.xlarge', 'g4dn.xlarge']
 * });
 */

var DEBOUNCE_DELAY = 500; // ms to wait before making API call

export var useInstanceAvailability = function(options) {
    var provider = options.provider;
    var credentialId = options.credentialId;
    var region = options.region;
    var zoneId = options.zoneId || null;
    var headNodeInstance = options.headNodeInstance;
    var computeNodeInstances = options.computeNodeInstances || [];
    var enabled = options.enabled !== undefined ? options.enabled : true;

    var dispatch = useDispatch();
    var debounceTimer = useRef(null);
    var lastParamsKey = useRef(null);

    // Get zone availability state from Redux store
    var zoneAvailabilityState = useSelector(function(state) {
        return state.clusters && state.clusters.cluster && state.clusters.cluster.zoneAvailability
            ? state.clusters.cluster.zoneAvailability
            : { zones: {}, loading: false, error: null, unableToVerify: false };
    });

    var zones = zoneAvailabilityState.zones || {};
    var isLoading = zoneAvailabilityState.loading || false;
    var error = zoneAvailabilityState.error || null;
    var unableToVerify = zoneAvailabilityState.unableToVerify || false;
    var cachedRegion = zoneAvailabilityState.region;
    var cachedInstanceTypes = zoneAvailabilityState.instanceTypes || [];
    

    // Collect all unique instance types
    var instanceTypes = useMemo(function() {
        var types = [];
        if (headNodeInstance) {
            types.push(headNodeInstance);
        }
        if (computeNodeInstances && Array.isArray(computeNodeInstances)) {
            for (var i = 0; i < computeNodeInstances.length; i++) {
                var inst = computeNodeInstances[i];
                if (inst && types.indexOf(inst) === -1) {
                    types.push(inst);
                }
            }
        }
        return types;
    }, [headNodeInstance, computeNodeInstances]);

    // Create a params key for dependency checking
    var paramsKey = useMemo(function() {
        return JSON.stringify({
            provider: provider,
            credentialId: credentialId,
            region: region,
            instanceTypes: instanceTypes.slice().sort()
        });
    }, [provider, credentialId, region, instanceTypes]);

    // Debounced fetch effect - only dispatch when paramsKey actually changes
    useEffect(function() {
        if (!enabled) {
            return;
        }

        if (!provider || !credentialId || !region || instanceTypes.length === 0) {
            return;
        }

        // Skip if params haven't actually changed (prevents infinite loops from array reference changes)
        if (lastParamsKey.current === paramsKey) {
            return;
        }

        // Clear previous timer
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }

        // Set new timer
        debounceTimer.current = setTimeout(function() {
            // Double-check paramsKey hasn't changed during debounce
            if (lastParamsKey.current !== paramsKey) {
                lastParamsKey.current = paramsKey;
                dispatch(Actions.checkZoneAvailability({
                    provider: provider,
                    credentialId: credentialId,
                    region: region,
                    instanceTypes: instanceTypes
                }));
            }
        }, DEBOUNCE_DELAY);

        // Cleanup
        return function() {
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
            }
        };
    }, [dispatch, enabled, paramsKey]);

    // Check if a specific zone is available
    var isZoneAvailable = useCallback(function(zoneId) {
        
        // If no availability data, assume available (don't block on error)
        if (!zones || Object.keys(zones).length === 0) {
            return true;
        }
        
        var zoneInfo = zones[zoneId];
        if (!zoneInfo) {
            // Zone not in data - could be new zone, assume available
            return true;
        }
        
        return zoneInfo.available === true;
    }, [zones]);

    // Get list of unavailable zones
    var unavailableZones = useMemo(function() {
        if (!zones) {
            return [];
        }
        var result = [];
        var zoneIds = Object.keys(zones);
        for (var i = 0; i < zoneIds.length; i++) {
            var zoneId = zoneIds[i];
            var zoneInfo = zones[zoneId];
            if (zoneInfo && zoneInfo.available === false) {
                result.push(zoneId);
            }
        }
        return result;
    }, [zones]);

    // Get missing instances for a zone
    var getMissingInstances = useCallback(function(zoneId) {
        if (!zones || !zones[zoneId]) {
            return [];
        }
        return zones[zoneId].missingInstances || [];
    }, [zones]);

    // Force refresh the availability data
    var refresh = useCallback(function() {
        dispatch(Actions.clearZoneAvailability());
        if (provider && credentialId && region && instanceTypes.length > 0) {
            dispatch(Actions.checkZoneAvailability({
                provider: provider,
                credentialId: credentialId,
                region: region,
                instanceTypes: instanceTypes
            }));
        }
    }, [dispatch, provider, credentialId, region, instanceTypes]);

    // Selected zone status (null = no data, true/false = available/unavailable)
    var selectedZoneData = (zoneId && !unableToVerify && zones) ? zones[zoneId] : null;
    var selectedZoneAvailable = selectedZoneData ? selectedZoneData.available : null;
    var selectedZoneMissing = selectedZoneData ? (selectedZoneData.missingInstances || []) : [];

    // Region-level availability (available if ANY zone is available)
    var regionAvailable = useMemo(function() {
        if (unableToVerify || !zones || Object.keys(zones).length === 0) return null;
        return Object.values(zones).some(function(z) { return z.available; });
    }, [zones, unableToVerify]);

    var regionMissingInstances = useMemo(function() {
        if (!zones || Object.keys(zones).length === 0) return [];
        return Object.values(zones).reduce(function(acc, z) {
            (z.missingInstances || []).forEach(function(inst) {
                if (acc.indexOf(inst) === -1) acc.push(inst);
            });
            return acc;
        }, []);
    }, [zones]);

    return {
        zoneAvailability: zones,
        isZoneAvailable: isZoneAvailable,
        unavailableZones: unavailableZones,
        getMissingInstances: getMissingInstances,
        isLoading: isLoading,
        error: error,
        unableToVerify: unableToVerify,
        refresh: refresh,
        instanceTypes: instanceTypes,
        selectedZoneAvailable: selectedZoneAvailable,
        selectedZoneMissing: selectedZoneMissing,
        regionAvailable: regionAvailable,
        regionMissingInstances: regionMissingInstances
    };
};

export default useInstanceAvailability;
